const OVERLAY_SELECTOR = 'dialog[data-avey-overlay]';
const OPEN_TRIGGER_SELECTOR = '[data-avey-overlay-open]';
const CLOSE_TRIGGER_SELECTOR = '[data-avey-overlay-close]';
const SCROLLABLE_SELECTOR = '[data-avey-overlay-scroll]';

class AveyOverlayManager {
  constructor() {
    this.activeOverlay = null;
    this.suspendedOverlays = [];
    this.returnFocusElement = null;
    this.lockedScrollPosition = null;
    this.lastTouchX = null;
    this.lastTouchY = null;
    this.transitionFallback = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.restoreDocumentPosition = this.restoreDocumentPosition.bind(this);

    document.addEventListener('click', this.handleClick);
    document.addEventListener('cancel', this.handleCancel, true);
    document.addEventListener('keydown', this.handleKeydown);
  }

  getOverlay(reference) {
    if (reference instanceof HTMLDialogElement) return reference;
    if (typeof reference !== 'string' || reference.length === 0) return null;

    const id = reference.startsWith('#') ? reference.slice(1) : reference;
    const overlay = document.getElementById(id);
    return overlay instanceof HTMLDialogElement && overlay.matches(OVERLAY_SELECTOR)
      ? overlay
      : null;
  }

  observeHeaders(scope = this.activeOverlay) {
    if (typeof ResizeObserver === 'undefined') return;
    // Observe the unstretched heading so the square can also shrink on resize.
    this.headerObserver ||= new ResizeObserver((entries) => {
      const updates = [];
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize;
        if (!height) continue;
        const header = entry.target.parentElement;
        const styles = getComputedStyle(header);
        const minimum = parseFloat(styles.minHeight) - parseFloat(styles.borderBottomWidth);
        const size = `${Math.ceil(Math.max(height, minimum))}px`;
        if (header.style.getPropertyValue('--avey-overlay-close-size') !== size) {
          updates.push({ header, size });
        }
      }
      if (updates.length) {
        this.headerResizeFrame = requestAnimationFrame(() => {
          this.headerResizeFrame = null;
          for (const { header, size } of updates) header.style.setProperty('--avey-overlay-close-size', size);
        });
      }
    });
    this.headerObserver.disconnect();
    if (this.headerResizeFrame) cancelAnimationFrame(this.headerResizeFrame);
    this.headerResizeFrame = null;
    scope?.querySelectorAll('.avey-overlay__heading, .avey-cart-shade-picker__title').forEach((heading) => {
      this.headerObserver.observe(heading, { box: 'border-box' });
    });
  }

  async open(reference, opener = document.activeElement, { stack = this.activeOverlay?.contains(opener) } = {}) {
    const overlay = this.getOverlay(reference);
    if (!overlay || overlay === this.activeOverlay) return;

    if (this.activeOverlay) {
      if (stack && !overlay.hasAttribute('data-avey-overlay-keep-header')) {
        this.suspendedOverlays.push({
          overlay: this.activeOverlay,
          returnFocusElement: this.returnFocusElement,
          lockedScrollPosition: this.lockedScrollPosition,
        });
      } else {
        const overlays = [this.activeOverlay, ...this.suspendedOverlays.map((entry) => entry.overlay).reverse()];
        for (const previous of overlays) await this.close(previous, { restoreFocus: false });
      }
    }

    this.activeOverlay = overlay;
    this.returnFocusElement = opener instanceof HTMLElement ? opener : null;
    if (this.suspendedOverlays.length === 0) {
      this.lockedScrollPosition = { left: window.scrollX, top: window.scrollY };
      this.lockDocument();
    }
    document.documentElement.dataset.aveyActiveOverlay = overlay.id;

    if (overlay.id === 'AveyCartDrawer') {
      // Preserve page width when the cart temporarily releases the root scrollbar gutter.
      const gutter = Math.max(0, window.innerWidth - document.documentElement.getBoundingClientRect().width);
      document.documentElement.style.setProperty('--avey-cart-page-gutter', `${gutter}px`);
    }

    overlay.dataset.state = 'opening';
    if (overlay.hasAttribute('data-avey-overlay-keep-header')) overlay.show();
    else overlay.showModal();

    this.observeHeaders(overlay);
    this.setTriggerState(overlay.id, true);

    await this.nextPaint();
    if (this.activeOverlay !== overlay || overlay.dataset.state !== 'opening') return;

    overlay.dataset.state = 'open';
    this.focusInitialElement(overlay);
    document.dispatchEvent(new CustomEvent('avey:overlay:open', { detail: { overlay } }));
  }

  async close(reference = this.activeOverlay, options = {}) {
    const overlay = this.getOverlay(reference);
    if (!overlay || !overlay.open) return;
    if (overlay.dataset.state === 'closing') {
      return new Promise((resolve) => overlay.addEventListener('close', resolve, { once: true }));
    }
    if (this.suspendedOverlays.some((entry) => entry.overlay === overlay)) {
      await this.close(this.activeOverlay, { restoreFocus: false });
      return this.close(overlay, options);
    }

    const { restoreFocus = true } = options;
    const returnFocusElement = this.returnFocusElement;
    overlay.dataset.state = 'closing';
    this.setTriggerState(overlay.id, false);
    await this.waitForMotion(overlay.querySelector('.avey-overlay__surface'));

    overlay.close();
    if (overlay.id === 'AveyCartDrawer') document.documentElement.style.removeProperty('--avey-cart-page-gutter');
    delete overlay.dataset.state;

    const parent = this.activeOverlay === overlay ? this.suspendedOverlays.pop() : null;
    if (this.activeOverlay === overlay) {
      this.activeOverlay = parent?.overlay || null;
      if (parent) {
        this.returnFocusElement = parent.returnFocusElement;
        this.lockedScrollPosition = parent.lockedScrollPosition;
        document.documentElement.dataset.aveyActiveOverlay = parent.overlay.id;
      } else {
        this.unlockDocument();
      }
    }

    if (restoreFocus && returnFocusElement?.isConnected) {
      returnFocusElement.focus({ preventScroll: true });
    } else if (returnFocusElement?.matches(':focus')) {
      returnFocusElement.blur();
    }
    if (!parent) {
      this.returnFocusElement = null;
      this.lockedScrollPosition = null;
    }
    this.observeHeaders();
    document.dispatchEvent(new CustomEvent('avey:overlay:close', { detail: { overlay } }));
  }

  requestClose(reference = this.activeOverlay, options = {}) {
    let overlay = this.getOverlay(reference);
    if (!overlay || !overlay.open) return;
    // A dismissal always removes one level, even when it targets a parent.
    if (this.suspendedOverlays.some((entry) => entry.overlay === overlay)) {
      overlay = this.activeOverlay;
    }

    const { restoreFocus = true, reason = 'dismiss' } = options;
    const requestEvent = new CustomEvent('avey:overlay:request-close', {
      bubbles: true,
      cancelable: true,
      detail: { overlay, reason },
    });
    if (!overlay.dispatchEvent(requestEvent)) return;

    return this.close(overlay, { restoreFocus });
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const openTrigger = target.closest(OPEN_TRIGGER_SELECTOR);
    if (openTrigger) {
      event.preventDefault();
      const overlay = this.getOverlay(openTrigger.dataset.aveyOverlayOpen);
      if (overlay?.open) {
        this.requestClose(overlay, { restoreFocus: event.detail === 0, reason: 'toggle' });
        if (event.detail > 0) openTrigger.blur();
        return;
      }
      this.open(openTrigger.dataset.aveyOverlayOpen, openTrigger, {
        stack: openTrigger.hasAttribute('data-avey-overlay-stack') || undefined,
      });
      return;
    }

    const closeTrigger = target.closest(CLOSE_TRIGGER_SELECTOR);
    if (closeTrigger) {
      event.preventDefault();
      this.requestClose(closeTrigger.closest(OVERLAY_SELECTOR), {
        restoreFocus: event.detail === 0,
        reason: 'close-button',
      });
      if (event.detail > 0) closeTrigger.blur();
      return;
    }

    if (target === this.activeOverlay || this.suspendedOverlays.some((entry) => entry.overlay === target)) {
      this.requestClose(target, {
        restoreFocus: event.detail === 0,
        reason: 'backdrop',
      });
    }
  }

  handleCancel(event) {
    const overlay = event.target.closest?.(OVERLAY_SELECTOR);
    if (!overlay) return;

    event.preventDefault();
    return this.requestClose(overlay, { reason: 'escape' });
  }

  handleKeydown(event) {
    if (!this.activeOverlay) return;
    if (event.key === 'Escape') {
      if (this.activeOverlay.matches(':modal')) return;
      event.preventDefault();
      this.requestClose(this.activeOverlay, { reason: 'escape' });
      return;
    }

    if (event.key !== 'Tab' || this.activeOverlay.matches(':modal')) return;
    const focusable = [...this.activeOverlay.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )].filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setTriggerState(overlayId, isOpen) {
    document.querySelectorAll(`${OPEN_TRIGGER_SELECTOR}[data-avey-overlay-open="${CSS.escape(overlayId)}"]`)
      .forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(isOpen));
        const label = isOpen ? trigger.dataset.closeLabel : trigger.dataset.openLabel;
        if (label) trigger.setAttribute('aria-label', label);
      });
  }

  lockDocument() {
    const root = document.documentElement;
    root.dataset.aveyOverlayState = 'open';
    window.addEventListener('scroll', this.restoreDocumentPosition, { passive: true, capture: true });

    document.addEventListener('touchstart', this.handleTouchStart, {
      passive: true,
      capture: true,
    });
    document.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
      capture: true,
    });
    document.addEventListener('wheel', this.handleWheel, {
      passive: false,
      capture: true,
    });
  }

  unlockDocument() {
    const root = document.documentElement;
    window.removeEventListener('scroll', this.restoreDocumentPosition, true);
    this.restoreDocumentPosition({ force: true });
    delete root.dataset.aveyOverlayState;
    delete root.dataset.aveyActiveOverlay;
    this.lastTouchX = null;
    this.lastTouchY = null;

    document.removeEventListener('touchstart', this.handleTouchStart, true);
    document.removeEventListener('touchmove', this.handleTouchMove, true);
    document.removeEventListener('wheel', this.handleWheel, true);
  }

  isAccountField(element = document.activeElement, overlay = this.activeOverlay) {
    return Boolean(
      overlay?.closest('.avey-account') &&
      overlay.contains(element) &&
      element?.matches('input, textarea, select, [contenteditable="true"]'),
    );
  }

  restoreDocumentPosition({ force = false } = {}) {
    if (!this.lockedScrollPosition) return;
    // Mobile browsers pan the viewport to expose focused form fields above the
    // keyboard. Search leaves that pan (including keyboard dismissal) to the
    // browser. Keep the CSS/gesture background lock and restore only on close.
    if (!force && (this.isAccountField() || this.activeOverlay?.id === 'AveySearchDialog')) return;
    const horizontalShift = Math.abs(window.scrollX - this.lockedScrollPosition.left);
    const verticalShift = Math.abs(window.scrollY - this.lockedScrollPosition.top);
    if (horizontalShift > 0.5 || verticalShift > 0.5) {
      window.scrollTo({ ...this.lockedScrollPosition, behavior: 'instant' });
    }
  }

  handleTouchStart(event) {
    if (!this.activeOverlay || event.touches.length !== 1) return;
    this.lastTouchX = event.touches[0].clientX;
    this.lastTouchY = event.touches[0].clientY;
  }

  handleTouchMove(event) {
    if (!this.activeOverlay || event.touches.length !== 1) return;

    const target = event.target instanceof Element ? event.target : null;
    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = this.lastTouchX === null ? 0 : currentX - this.lastTouchX;
    const deltaY = this.lastTouchY === null ? 0 : currentY - this.lastTouchY;
    const horizontalScroller = target?.closest('[data-avey-horizontal-scroll]');

    if (
      horizontalScroller &&
      this.activeOverlay.contains(horizontalScroller) &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      this.lastTouchX = currentX;
      this.lastTouchY = currentY;
      return;
    }

    const scrollable = target?.closest(SCROLLABLE_SELECTOR);
    if (!scrollable || !this.activeOverlay.contains(scrollable)) {
      event.preventDefault();
      return;
    }

    const atTop = scrollable.scrollTop <= 0;
    const atBottom =
      scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;

    if ((deltaY > 0 && atTop) || (deltaY < 0 && atBottom)) event.preventDefault();
    this.lastTouchX = currentX;
    this.lastTouchY = currentY;
  }

  handleWheel(event) {
    if (!this.activeOverlay) return;

    const target = event.target instanceof Element ? event.target : null;
    const scrollable = target?.closest(SCROLLABLE_SELECTOR);
    if (!scrollable || !this.activeOverlay.contains(scrollable)) {
      event.preventDefault();
      return;
    }

    const atTop = scrollable.scrollTop <= 0;
    const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;
    if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) event.preventDefault();
  }

  focusInitialElement(overlay) {
    // A tap during the opening paint must not be stolen by delayed autofocus.
    if (this.isAccountField(document.activeElement, overlay)) return;
    const initialFocus = overlay.querySelector('[autofocus], [data-avey-overlay-initial-focus]');
    if (initialFocus instanceof HTMLElement) {
      initialFocus.focus({ preventScroll: !this.isAccountField(initialFocus, overlay) });
    }
  }

  nextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  waitForMotion(element) {
    if (!(element instanceof HTMLElement)) return Promise.resolve();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return Promise.resolve();
    }

    const durations = getComputedStyle(element).transitionDuration
      .split(',')
      .map((value) => Number.parseFloat(value) * (value.includes('ms') ? 1 : 1000));
    const duration = Math.max(0, ...durations);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (event) => {
        if (event && (event.target !== element || event.propertyName !== 'transform')) return;
        if (settled) return;
        settled = true;
        element.removeEventListener('transitionend', finish);
        window.clearTimeout(this.transitionFallback);
        resolve();
      };

      element.addEventListener('transitionend', finish);
      this.transitionFallback = window.setTimeout(finish, duration + 80);
    });
  }
}

window.AveyOverlayManager = new AveyOverlayManager();
